import React, { useState } from 'react';
import { ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
import { RecordModal } from './RecordModal';
interface StageRecord {
  id: string;
  borrowerName: string;
  loanAmount: number;
  date: string;
  status: string;
  propertyAddress?: string;
}
interface PerformanceData {
  bucket: string;
  teamMember?: string;
  dials: number;
  dialsTransferPercent: number;
  transfers: number;
  apps: number;
  appDocPercent: number;
  docs: number;
  docSubPercent: number;
  subs: number;
  subClosingPercent: number;
  closings: number;
  totalCompensation: number;
  totalLoanAmount: number;
  avgComp: number;
  dialsRecords?: StageRecord[];
  transfersRecords?: StageRecord[];
  appsRecords?: StageRecord[];
  docsRecords?: StageRecord[];
  subsRecords?: StageRecord[];
  closingsRecords?: StageRecord[];
}
interface PerformanceTableProps {
  data: PerformanceData[];
  viewMode: 'aggregated' | 'individual';
}
type SortField = 'bucket' | 'teamMember' | 'dials' | 'transfers' | 'apps' | 'docs' | 'subs' | 'closings' | 'avgComp';
type SortDirection = 'asc' | 'desc';
type Stage = 'Dials' | 'Transfers' | 'Apps' | 'Docs' | 'Submissions' | 'Closings';
export function PerformanceTable({
  data,
  viewMode
}: PerformanceTableProps) {
  const [sortField, setSortField] = useState<SortField>('dials');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    stage: Stage | null;
    records: StageRecord[];
    bucketName: string;
  }>({
    isOpen: false,
    stage: null,
    records: [],
    bucketName: ''
  });
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const openModal = (stage: Stage, records: StageRecord[] = [], bucketName: string) => {
    setModalState({
      isOpen: true,
      stage,
      records,
      bucketName
    });
  };
  const closeModal = () => {
    setModalState({
      isOpen: false,
      stage: null,
      records: [],
      bucketName: ''
    });
  };
  const sortedData = [...data].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.localeCompare(bValue) * multiplier;
    }
    return ((aValue as number) - (bValue as number)) * multiplier;
  });
  const getPercentageColor = (percent: number) => {
    if (percent >= 50) return 'text-green-700 bg-green-50 border-green-200';
    if (percent >= 25) return 'text-amber-700 bg-amber-50 border-amber-200';
    return 'text-red-700 bg-red-50 border-red-200';
  };
  const getCompColor = (comp: number) => {
    if (comp >= 2.5) return 'text-green-700 bg-green-50 border-green-200';
    if (comp >= 1.5) return 'text-amber-700 bg-amber-50 border-amber-200';
    return 'text-red-700 bg-red-50 border-red-200';
  };
  const formatAvgComp = (avgComp: number, totalLoanAmount: number) => {
    if (!totalLoanAmount || totalLoanAmount === 0) {
      return <span className="text-sm text-gray-400 font-medium" data-id="element-42">N/A</span>;
    }
    return <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${getCompColor(avgComp)}`} data-id="element-43">
        {avgComp.toFixed(1)}%
      </span>;
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return <ArrowUpIcon className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-44" />;
    return sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-blue-600" data-id="element-45" /> : <ArrowDownIcon className="h-4 w-4 text-blue-600" data-id="element-46" />;
  };
  return <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-47">
        <div className="overflow-x-auto" data-id="element-48">
          <table className="w-full" data-id="element-49">
            <thead data-id="element-50">
              <tr className="bg-gradient-to-b from-gray-50 to-gray-100 border-b border-gray-200" data-id="element-51">
                <th className="px-6 py-4 text-left" data-id="element-52">
                  <button onClick={() => handleSort(viewMode === 'individual' ? 'teamMember' : 'bucket')} className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wide hover:text-gray-900 group transition-colors" data-id="element-53">
                    {viewMode === 'individual' ? 'Team Member' : 'Bucket'}
                    <SortIcon field={viewMode === 'individual' ? 'teamMember' : 'bucket'} data-id="element-54" />
                  </button>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-55">
                  <button onClick={() => handleSort('dials')} className="flex items-center justify-end gap-2 text-xs font-bold text-gray-700 uppercase tracking-wide hover:text-gray-900 group w-full transition-colors" data-id="element-56">
                    Dials
                    <SortIcon field="dials" data-id="element-57" />
                  </button>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-58">
                  <div className="text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-59">
                    Dials → Transfer %
                  </div>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-60">
                  <button onClick={() => handleSort('transfers')} className="flex items-center justify-end gap-2 text-xs font-bold text-gray-700 uppercase tracking-wide hover:text-gray-900 group w-full transition-colors" data-id="element-61">
                    Transfers
                    <SortIcon field="transfers" data-id="element-62" />
                  </button>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-63">
                  <button onClick={() => handleSort('apps')} className="flex items-center justify-end gap-2 text-xs font-bold text-gray-700 uppercase tracking-wide hover:text-gray-900 group w-full transition-colors" data-id="element-64">
                    Apps
                    <SortIcon field="apps" data-id="element-65" />
                  </button>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-66">
                  <div className="text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-67">
                    App → Doc %
                  </div>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-68">
                  <button onClick={() => handleSort('docs')} className="flex items-center justify-end gap-2 text-xs font-bold text-gray-700 uppercase tracking-wide hover:text-gray-900 group w-full transition-colors" data-id="element-69">
                    Docs
                    <SortIcon field="docs" data-id="element-70" />
                  </button>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-71">
                  <div className="text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-72">
                    Doc → Sub %
                  </div>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-73">
                  <button onClick={() => handleSort('subs')} className="flex items-center justify-end gap-2 text-xs font-bold text-gray-700 uppercase tracking-wide hover:text-gray-900 group w-full transition-colors" data-id="element-74">
                    Subs
                    <SortIcon field="subs" data-id="element-75" />
                  </button>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-76">
                  <div className="text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-77">
                    Sub → Closing %
                  </div>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-78">
                  <button onClick={() => handleSort('closings')} className="flex items-center justify-end gap-2 text-xs font-bold text-gray-700 uppercase tracking-wide hover:text-gray-900 group w-full transition-colors" data-id="element-79">
                    Closings
                    <SortIcon field="closings" data-id="element-80" />
                  </button>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-81">
                  <button onClick={() => handleSort('avgComp')} className="flex items-center justify-end gap-2 text-xs font-bold text-gray-700 uppercase tracking-wide hover:text-gray-900 group w-full transition-colors" data-id="element-82">
                    Avg. Comp %
                    <SortIcon field="avgComp" data-id="element-83" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-84">
              {sortedData.map((row, index) => <tr key={index} className="hover:bg-gray-50 transition-colors" data-id="element-85">
                  <td className="px-6 py-4" data-id="element-86">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-87">
                      {viewMode === 'individual' ? row.teamMember : row.bucket}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-88">
                    <button onClick={() => openModal('Dials', row.dialsRecords, viewMode === 'individual' ? row.teamMember! : row.bucket)} className="text-sm font-bold text-blue-600 hover:text-blue-800 hover:underline transition-colors" data-id="element-89">
                      {row.dials.toLocaleString()}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-90">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${getPercentageColor(row.dialsTransferPercent)}`} data-id="element-91">
                      {row.dialsTransferPercent}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-92">
                    <button onClick={() => openModal('Transfers', row.transfersRecords, viewMode === 'individual' ? row.teamMember! : row.bucket)} className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors" data-id="element-93">
                      {row.transfers.toLocaleString()}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-94">
                    <button onClick={() => openModal('Apps', row.appsRecords, viewMode === 'individual' ? row.teamMember! : row.bucket)} className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors" data-id="element-95">
                      {row.apps.toLocaleString()}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-96">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${getPercentageColor(row.appDocPercent)}`} data-id="element-97">
                      {row.appDocPercent}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-98">
                    <button onClick={() => openModal('Docs', row.docsRecords, viewMode === 'individual' ? row.teamMember! : row.bucket)} className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors" data-id="element-99">
                      {row.docs.toLocaleString()}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-100">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${getPercentageColor(row.docSubPercent)}`} data-id="element-101">
                      {row.docSubPercent}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-102">
                    <button onClick={() => openModal('Submissions', row.subsRecords, viewMode === 'individual' ? row.teamMember! : row.bucket)} className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors" data-id="element-103">
                      {row.subs.toLocaleString()}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-104">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${getPercentageColor(row.subClosingPercent)}`} data-id="element-105">
                      {row.subClosingPercent}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-106">
                    <button onClick={() => openModal('Closings', row.closingsRecords, viewMode === 'individual' ? row.teamMember! : row.bucket)} className="text-sm font-bold text-blue-600 hover:text-blue-800 hover:underline transition-colors" data-id="element-107">
                      {row.closings.toLocaleString()}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-108">
                    {formatAvgComp(row.avgComp, row.totalLoanAmount)}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <RecordModal isOpen={modalState.isOpen} onClose={closeModal} stage={modalState.stage || 'Dials'} records={modalState.records} bucketName={modalState.bucketName} data-id="element-109" />
    </>;
}